import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search } from 'lucide-react';

export interface SearchableOption {
  value: string;
  label: string;
  /** Optional logo/icon URL rendered to the left of the label. */
  logo?: string | null;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: SearchableOption[];
  /** Label for the empty/"all" choice. */
  allLabel?: string;
  placeholder?: string;
}

/**
 * Typeable single-select used for the domain list's column filters. Renders a
 * compact trigger (matching the old <select>) and a portal-positioned, search-
 * filterable menu so it isn't clipped by the table's horizontal scroll. Options
 * may carry a logo (e.g. registrar favicons).
 */
export default function SearchableSelect({ value, onChange, options, allLabel = 'All', placeholder = 'Type to filter…' }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null);

  const selected = options.find((o) => o.value === value) || null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q));
  }, [options, query]);

  // Position the menu under the trigger (fixed coords; survives table scroll).
  const reposition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 220) });
  };

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    const onScrollOrResize = () => reposition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => { if (!open) { setQuery(''); setActiveIdx(0); } }, [open]);
  useEffect(() => { setActiveIdx(0); }, [query]);

  const pick = (v: string) => { onChange(v); setOpen(false); };

  // Rows in the menu: the "All" clear row, then filtered options.
  const rows: (SearchableOption | { value: ''; label: string; logo?: null })[] =
    [{ value: '', label: allLabel }, ...filtered];

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, rows.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); const r = rows[activeIdx]; if (r) pick(r.value); }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center gap-1 text-xs border rounded px-1.5 py-1 bg-surface transition-colors text-left ${
          value ? 'border-brand-400 bg-brand-50/40' : 'border-slate-200'
        } focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none`}
      >
        {selected?.logo && <img src={selected.logo} alt="" className="h-3.5 w-3.5 rounded-sm shrink-0 object-contain" />}
        <span className={`flex-1 truncate ${value ? 'text-slate-700' : 'text-slate-400'}`}>
          {selected ? selected.label : allLabel}
        </span>
        <ChevronDown className="h-3 w-3 text-slate-400 shrink-0" />
      </button>

      {open && rect && createPortal(
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: rect.top, left: rect.left, width: rect.width, zIndex: 50 }}
          className="bg-surface border border-slate-200 rounded-md shadow-lg overflow-hidden"
        >
          <div className="p-1.5 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                className="w-full text-xs border border-slate-200 rounded pl-7 pr-2 py-1 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none"
              />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {rows.map((r, idx) => {
              const isSelected = r.value === value;
              const isActive = idx === activeIdx;
              const logo = 'logo' in r ? r.logo : null;
              return (
                <button
                  key={r.value || '__all__'}
                  type="button"
                  onMouseEnter={() => setActiveIdx(idx)}
                  onClick={() => pick(r.value)}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-left ${
                    isActive ? 'bg-brand-50' : ''
                  } ${isSelected ? 'font-medium text-brand-700' : 'text-slate-700'}`}
                >
                  {logo
                    ? <img src={logo} alt="" className="h-4 w-4 rounded-sm shrink-0 object-contain" />
                    : <span className="h-4 w-4 shrink-0" />}
                  <span className="flex-1 truncate">{r.label}</span>
                  {isSelected && <Check className="h-3.5 w-3.5 text-brand-600 shrink-0" />}
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="px-2.5 py-3 text-xs text-slate-400 text-center">No matches</div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
